import { c as _c } from "react/compiler-runtime";
import ActionCard from '@precis-digital/kurama/ActionCard';
import CancelIcon from '@precis-digital/kurama/icons/CancelIcon';
import CheckIcon from '@precis-digital/kurama/icons/CheckIcon';
import Chip from '@precis-digital/kurama/Chip';
import Grid from '@precis-digital/kurama/Grid';
import Notification from '@precis-digital/kurama/Notification';
import Typography from '@precis-digital/kurama/Typography';
import { styled } from '@precis-digital/kurama/utils';
import { type ReactElement } from 'react';
import { useCopilot } from 'shared/context/CopilotContext';
import { useTranslation } from 'shared/translations';
import { buildHitlDecisionActions, useCopilotHitlDecision, type CopilotHitlDecision } from '../hitlDecision';
import { buildPlanApprovalUserMessage, parsePlanApprovalArgs, parsePlanApprovalResult } from './parsePlanApproval';
export interface PlanApprovalCardProps {
  args: Record<string, unknown>;
  toolCallId: string;
  toolName?: string;
  /** Pending (or later) long-running tool result content from AG-UI. */
  resultContent?: unknown;
}

/**
 * Visible card for `submit_plan_for_approval`. Approve/Reject send a plain
 * user message rather than resuming the long-running tool via AG-UI `role: tool`.
 */
const PlanApprovalCard = t0 => {
  const $ = _c(34);
  const {
    args,
    resultContent
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "copilot.dataAgent.planApproval"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("common", t1);
  const {
    sendUserMessage
  } = useCopilot();
  const {
    userRequest,
    steps
  } = parsePlanApprovalArgs(args);
  let t2;
  if ($[1] !== resultContent) {
    t2 = parsePlanApprovalResult(resultContent);
    $[1] = resultContent;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const parsedResult = t2;
  const {
    decision,
    startDecision,
    finishDecision
  } = useCopilotHitlDecision(parsedResult.status);
  let t3;
  if ($[3] !== finishDecision || $[4] !== sendUserMessage || $[5] !== startDecision) {
    t3 = next => {
      if (!startDecision(next, {
        isAsync: true
      })) {
        return;
      }
      const sent = sendUserMessage(buildPlanApprovalUserMessage(next), {
        includeComposerAttachments: false
      });
      finishDecision(sent ? next : null);
    };
    $[3] = finishDecision;
    $[4] = sendUserMessage;
    $[5] = startDecision;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleDecision = t3;
  const T0 = ActionCard;
  let t4;
  if ($[7] !== t) {
    t4 = t("title");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = "semantic-info";
  const T1 = StyledDescription;
  const t6 = userRequest.trim().length > 0 ? <StyledSection><Typography variant="body3" color="secondary">{t("requestLabel")}</Typography><Typography variant="body2">{userRequest}</Typography></StyledSection> : null;
  const T2 = StyledSection;
  let t7;
  if ($[9] !== t) {
    t7 = t("stepsLabel");
    $[9] = t;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = <Typography variant="body3" color="secondary">{t7}</Typography>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = steps.length === 0 ? <Typography variant="body2" color="secondary">{t("noSteps")}</Typography> : <StyledStepsList>{steps.map(_temp)}</StyledStepsList>;
  let t10;
  if ($[13] !== T2 || $[14] !== t8 || $[15] !== t9) {
    t10 = <T2>{t8}{t9}</T2>;
    $[13] = T2;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== T1 || $[18] !== t10 || $[19] !== t6) {
    t11 = <T1>{t6}{t10}</T1>;
    $[17] = T1;
    $[18] = t10;
    $[19] = t6;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== decision || $[22] !== handleDecision || $[23] !== t) {
    t12 = decision != null ? undefined : buildHitlDecisionActions({
      onDecision: handleDecision,
      approveLabel: t("approve"),
      rejectLabel: t("reject")
    });
    $[21] = decision;
    $[22] = handleDecision;
    $[23] = t;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== decision || $[26] !== t) {
    t13 = decision != null ? <Chip avatar={decision === "approved" ? <CheckIcon /> : <CancelIcon />} label={decision === "approved" ? t("approvedChip") : t("rejectedChip")} variant={decision === "approved" ? "semantic-success" : "semantic-neutral"} /> : undefined;
    $[25] = decision;
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== T0 || $[29] !== t11 || $[30] !== t12 || $[31] !== t13 || $[32] !== t4) {
    t14 = <T0 title={t4} variant={t5} description={t11} actions={t12} actionsEmptyState={t13} />;
    $[28] = T0;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t4;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
};
const StyledDescription = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%'
}));
const StyledSection = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%'
}));
const StyledStepsList = styled('ol')(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  margin: 0,
  padding: 0,
  listStyle: 'none',
  width: '100%'
}));
const StyledStepItem = styled('li')(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  width: '100%'
}));
const StyledStepNumber = styled('span')({
  flexShrink: 0,
  display: 'inline-flex',
  lineHeight: 0
});
export default PlanApprovalCard;
function _temp(step, index) {
  return <StyledStepItem key={index}><StyledStepNumber><Notification variant="tonal" value={(index + 1).toString()} /></StyledStepNumber><Typography variant="body3">{step}</Typography></StyledStepItem>;
}